import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

type Rollout = {
  productAngleWorkflowUrl: string | null;
  enabledCount: number;
  brands: { brandId: string; brandName: string; enabled: boolean }[];
  unavailableEnabledBrands: { brandId: string; brandName: string }[];
};
const key = ["/api/admin/narrative-v2-rollout"];

function errorMessage(error: unknown) {
  const raw = error instanceof Error ? error.message : "Request failed";
  try { return JSON.parse(raw.replace(/^\d+:\s*/, "")).message || raw; } catch { return raw; }
}

export function NarrativeV2Rollout() {
  const query = useQuery<Rollout>({
    queryKey: key,
    queryFn: async () => (await apiRequest("GET", key[0])).json(),
  });
  const [url, setUrl] = useState("");
  const [search, setSearch] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => { if (query.data) setUrl(query.data.productAngleWorkflowUrl || ""); }, [query.data]);

  const save = useMutation({
    mutationFn: async (value: string) => {
      await apiRequest("PUT", key[0], { productAngleWorkflowUrl: value });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      setError(""); setStatus("Shared URL saved.");
    },
    onError: (e) => { setError(errorMessage(e)); setStatus(""); },
  });
  const toggle = useMutation({
    mutationFn: async ({ brandId, enabled }: { brandId: string; enabled: boolean }) => {
      await apiRequest("PUT", `${key[0]}/brands/${encodeURIComponent(brandId)}`, { enabled });
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: key });
      setError(""); setStatus(`Brand ${variables.enabled ? "enabled" : "disabled"}.`);
    },
    onError: (e) => { setError(errorMessage(e)); setStatus(""); },
  });
  const submit = () => {
    if (!url.trim() && query.data?.productAngleWorkflowUrl) { setConfirmClear(true); return; }
    save.mutate(url);
  };
  const busy = save.isPending || toggle.isPending;
  const filtered = query.data?.brands.filter((brand) => brand.brandName.toLowerCase().includes(search.toLowerCase())) || [];

  return <Card data-testid="narrative-v2-rollout">
    <CardHeader>
      <CardTitle>Narrative V2 · Product angles</CardTitle>
      <CardDescription>One Sim V2 execute URL for enabled brands only. Opportunity analysis and offering angles are unchanged. Existing angles are not rerun.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-5">
      {query.isLoading ? <p>Loading rollout settings…</p> : query.isError ? (
        <div role="alert">Could not load settings: {errorMessage(query.error)} <Button variant="outline" onClick={() => query.refetch()}>Retry</Button></div>
      ) : <>
        <div className="space-y-2">
          <Label htmlFor="narrative-v2-url">Shared Sim V2 execute URL</Label>
          <Input id="narrative-v2-url" value={url} onChange={(e) => { setUrl(e.target.value); setStatus(""); }}
            placeholder="https://www.sim.ai/api/v2/workflows/…/execute" disabled={busy} />
          <p className="text-xs text-muted-foreground">Save the URL before enabling any brands. Disable all brands before clearing it.</p>
          <Button onClick={submit} disabled={busy || url === (query.data?.productAngleWorkflowUrl || "")}>
            {save.isPending ? "Saving…" : "Save shared URL"}
          </Button>
        </div>
        <div className="space-y-3 border-t pt-4">
          <div className="font-medium">Brands ({query.data?.enabledCount || 0} enabled)</div>
          <Input aria-label="Search brands" placeholder="Search brands" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filtered.map((brand) => <div key={brand.brandId} className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor={`narrative-v2-${brand.brandId}`}>{brand.brandName}</Label>
              <Switch id={`narrative-v2-${brand.brandId}`} checked={brand.enabled}
                disabled={busy || (!brand.enabled && (!query.data?.productAngleWorkflowUrl || url !== query.data.productAngleWorkflowUrl))}
                onCheckedChange={(enabled) => toggle.mutate({ brandId: brand.brandId, enabled })} />
            </div>)}
            {!filtered.length && <p className="text-sm text-muted-foreground">No eligible brands match.</p>}
          </div>
          {!!query.data?.unavailableEnabledBrands.length && <div className="space-y-2 border-t pt-3">
            <p className="text-sm text-muted-foreground">No longer eligible — disable these before clearing the URL:</p>
            {query.data.unavailableEnabledBrands.map((brand) => <div key={brand.brandId} className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor={`narrative-v2-unavailable-${brand.brandId}`}>{brand.brandName}</Label>
              <Switch id={`narrative-v2-unavailable-${brand.brandId}`} checked disabled={busy}
                onCheckedChange={() => toggle.mutate({ brandId: brand.brandId, enabled: false })} />
            </div>)}
          </div>}
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {status && <p role="status" className="text-sm text-muted-foreground">{status}</p>}
      </>}
    </CardContent>
    <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Clear shared V2 URL?</AlertDialogTitle>
          <AlertDialogDescription>This cannot be cleared while any brands are enabled. Disable them first; their previous routing will be restored.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={!!query.data?.enabledCount} onClick={() => save.mutate("")}>Clear URL</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </Card>;
}